import React from 'react';
import { Box, Stack, Typography, Paper, Chip, Avatar } from '@mui/material';
import { FileText, Mail, Clock, Quote } from 'lucide-react';

export interface INote {
  panelist: string;
  notes: string;
  createdAt?: string;
  panelistInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface INotes {
  data: INote[];
  panelistsMap?: Record<string, any>;
}

export const Notes: React.FC<INotes> = ({ data, panelistsMap = {} }) => {
  if (!data || data.length === 0) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ py: 8, px: 2 }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(to bottom right, #eff6ff, #e0e7ff)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <FileText size={40} style={{ color: '#3b82f6' }} />
        </Box>
        <Typography variant='h6' sx={{ color: 'text.primary', mb: 1, fontWeight: 600 }}>
          No Panelist Notes Yet
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 360 }}>
          Panelist feedback and observations will appear here after they submit
          their notes
        </Typography>
      </Stack>
    );
  }

  const colors = [
    '#3b82f6',
    '#8b5cf6',
    '#10b981',
    '#f97316',
    '#ec4899',
    '#14b8a6',
  ];

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 3,
        width: '100%',
        bgcolor: 'action.hover',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography
          variant='caption'
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FileText size={16} style={{ color: '#3b82f6' }} />
          Interview Notes ({data.length})
        </Typography>
      </Box>

      <Stack spacing={2}>
        {data.map((note, index) => {
          const panelistId =
            typeof note.panelist === 'string' ? note.panelist : '';
          const panelistInfo = panelistsMap[panelistId] || note.panelistInfo;

          const panelistName = panelistInfo
            ? `${panelistInfo.firstName || ''} ${
                panelistInfo.lastName || ''
              }`.trim()
            : panelistId || `Panelist ${index + 1}`;

          const panelistInitials = panelistInfo
            ? `${panelistInfo.firstName?.[0] || ''}${
                panelistInfo.lastName?.[0] || ''
              }`.toUpperCase()
            : panelistName.substring(0, 2).toUpperCase();

          const accentColor = colors[index % colors.length];

          return (
            <Paper
              key={index}
              variant='outlined'
              sx={{
                position: 'relative',
                borderRadius: 2,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 2,
                },
              }}
            >
              {/* Left accent bar */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 4,
                  height: '100%',
                  bgcolor: accentColor,
                }}
              />

              <Box sx={{ p: 2.5, pl: 3 }}>
                <Stack spacing={2}>
                  {/* Panelist info header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: accentColor,
                        fontWeight: 700,
                        fontSize: '0.875rem',
                      }}
                    >
                      {panelistInitials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                          {panelistName}
                        </Typography>
                        <Chip
                          label='Panelist'
                          size='small'
                          sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            bgcolor: 'primary.light',
                            color: 'primary.dark',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                        {panelistInfo?.email && (
                          <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <Mail size={12} />
                            {panelistInfo.email}
                          </Typography>
                        )}
                        {note.createdAt && (
                          <Typography
                            variant='caption'
                            sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <Clock size={12} />
                            {new Date(note.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Note content */}
                  <Box sx={{ display: 'flex', gap: 1.5, pl: 1 }}>
                    <Quote size={20} style={{ color: '#93c5fd', flexShrink: 0, marginTop: 2 }} />
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {note.notes}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default Notes;
